import { NODE_KINDS, kindOf, kindMeta } from 'features/evals3/editor/nodeKinds';
import { TEMPLATES } from 'features/evals3/templates';

/**
 * A simple eval: one check, one answer, a rule for which answers pass.
 *
 * It is the same graph the triage editor draws — one step at the entry, its
 * edges to a Pass and a Fail verdict — read back as a form, so a person who
 * never needs a second step never sees a canvas, and one who does can open
 * the same eval in Triage and keep going. NOTHING HERE CHANGES THE WIRE
 * FORMAT: the executor, the validator and the analysis read the graph.
 *
 *   answer   the step's one output: true/false, a score in a range, or a
 *            category (one of some values)
 *   passes   boolean → which value passes; number → the range that passes
 *            (inclusive both ends, inside the declared range); enum → the
 *            values that pass. Everything else fails.
 *
 * A Decision model check is the one kind with several answers: one enum per
 * question. Its rule is a pass set per question, and it passes only when
 * every answer is in its set — written as one "not in the pass set → Fail"
 * edge per question and a default to Pass. Can't tell is never in a pass set
 * unless someone put it there, so an unsure answer fails.
 */

export const STEP = 'check';
export const PASS = 'pass';
export const FAIL = 'fail';
export const SIMPLE_KINDS = ['llm_judge', 'decision_model', 'semantic', 'python', 'human'];   // no Filter: the eval's own filters already decide what is scored
export const ANSWERS = [
  { value: 'boolean', label: 'True / false' },
  { value: 'number', label: 'Score' },
  { value: 'enum', label: 'Category' },
];
/** Kinds whose answer shape is the instrument's own, not the person's to change. */
export const FIXED_ANSWER = { semantic: 'boolean', ui_filter: 'boolean', human: 'enum', decision_model: 'decision' };
const DEFAULT_REASON = { python: 'CHECK_FAILED', llm_judge: 'LOW_SCORE', decision_model: 'CHECK_FAILED', semantic: 'CHECK_FAILED', ui_filter: 'NO_MATCH', human: 'REJECTED' };
const TYPE_REASON = { number: 'LOW_SCORE', enum: 'NOT_ACCEPTED', boolean: 'CHECK_FAILED' };
const DEFAULT_REASONS = new Set(['', 'FAILED', ...Object.values(DEFAULT_REASON), ...Object.values(TYPE_REASON)]);

const tidy = (n) => Number(Number(n).toFixed(6));

/** The one output field of a node: [name, spec] or null. */
function fieldOf(node) {
  const entries = Object.entries(node?.output || {});
  return entries.length === 1 ? entries[0] : null;
}

/** The rule the edges express, or null when they say something the form cannot. */
function ruleOf(node, field, spec) {
  const edges = node.edges || [];
  const to = edges.filter((e) => e.default === undefined);
  const dflt = edges.find((e) => e.default !== undefined);
  if (!dflt) return null;
  const passEdges = to.filter((e) => e.to === PASS);
  const failEdges = to.filter((e) => e.to === FAIL);
  if (to.length !== passEdges.length + failEdges.length) return null;   // an edge to a third node
  // Either the pass edges are named and the default fails, or the fail edges are named and the default passes.
  const named = dflt.default === FAIL ? passEdges : dflt.default === PASS ? failEdges : null;
  if (!named || (named.length !== to.length)) return null;
  const passes = dflt.default === FAIL;
  if (spec.type === 'boolean') {
    if (named.length !== 1 || named[0].when.field !== field || named[0].when.op !== 'eq') return null;
    return { type: 'boolean', value: passes ? named[0].when.value : !named[0].when.value };
  }
  if (spec.type === 'enum') {
    const values = [];
    for (const e of named) {
      if (e.when.field !== field) return null;
      if (e.when.op === 'eq') values.push(e.when.value);
      else if (e.when.op === 'in') values.push(...e.when.value);
      else return null;
    }
    const chosen = passes ? values : spec.values.filter((v) => !values.includes(v));
    return { type: 'enum', values: chosen };
  }
  if (named.length !== 1 || named[0].when.field !== field) return null;
  const w = named[0].when;
  let min; let max;
  if (w.op === 'between') { min = w.range.min; max = w.range.max; }
  else if (w.op === 'gte') { min = w.value; max = spec.max; }
  else if (w.op === 'lte') { min = spec.min; max = w.value; }
  else return null;
  if (!passes) return null;   // "fails when in range" has no single pass range to show
  return { type: 'number', min: tidy(min), max: tidy(max) };
}

// ---------------------------------------------------------------------------
// Decision model: one pass set per question
// ---------------------------------------------------------------------------

const CANT_TELL = 'cant_tell';

/** What passes by default for one question's outcomes: yes, or the first option. */
export function defaultPasses(values = []) {
  const real = values.filter((v) => v !== CANT_TELL);
  if (real.includes('yes') && real.includes('no')) return ['yes'];
  return real.slice(0, 1);
}

/** The values a condition on one of these fields matches, or null when it is not one the form can read. */
function matchedBy(when, output) {
  const spec = when && output[when.field];
  if (!spec) return null;
  const vs = spec.values || [];
  if (when.op === 'eq') return [when.value];
  if (when.op === 'neq') return vs.filter((v) => v !== when.value);
  if (when.op === 'in' && Array.isArray(when.value)) return when.value;
  if (when.op === 'not_in' && Array.isArray(when.value)) return vs.filter((v) => !when.value.includes(v));
  return null;
}

/** A score question's default pass range: the upper half of the scale. */
export function defaultRange(spec) {
  return { min: Math.round(((spec.min + spec.max) / 2) * 100) / 100, max: spec.max };
}

/** A decision step's rule from its edges (already mapped to PASS / FAIL), or null. */
function decisionRuleOf(output, edges) {
  const keys = Object.keys(output || {});
  if (!keys.length) return null;
  const dflt = edges.find((e) => e.default !== undefined);
  if (!dflt) return null;
  const named = edges.filter((e) => e.default === undefined);
  const all = (k) => output[k].values || [];
  const isNum = (k) => output[k] && output[k].type === 'number';
  const passes = Object.fromEntries(keys.map((k) => [k, isNum(k) ? { min: output[k].min, max: output[k].max } : all(k).slice()]));
  if (dflt.default === PASS) {
    // Each named edge fails what it matches: an outcome set, or a score below / above its pass range.
    for (const e of named) {
      if (e.to !== FAIL || !e.when) return null;
      const k = e.when.field;
      if (isNum(k)) {
        if (e.when.op === 'lt') passes[k] = { ...passes[k], min: e.when.value };
        else if (e.when.op === 'gt') passes[k] = { ...passes[k], max: e.when.value };
        else return null;
        continue;
      }
      const m = matchedBy(e.when, output);
      if (!m) return null;
      passes[k] = passes[k].filter((v) => !m.includes(v));
    }
  } else if (dflt.default === FAIL) {
    if (keys.some(isNum)) return null;
    // One question names what passes; the rest may be anything.
    if (named.length > 1 || (named[0] && named[0].to !== PASS)) return null;
    if (!named.length) passes[keys[0]] = [];
    else {
      const m = matchedBy(named[0].when, output);
      if (!m) return null;
      passes[named[0].when.field] = all(named[0].when.field).filter((v) => m.includes(v));
    }
  } else return null;
  return { type: 'decision', passes };
}

/** The edges for a decision rule: an answer outside its pass set fails; the rest pass. */
export function decisionEdges(output, rule, passId = PASS, failId = FAIL) {
  const edges = [];
  for (const [k, spec] of Object.entries(output || {})) {
    if (spec.type === 'number') {
      // A score passes inside its range: below it or above it fails.
      const r = (rule && rule.passes && rule.passes[k] && !Array.isArray(rule.passes[k])) ? rule.passes[k] : defaultRange(spec);
      if (r.min > spec.min) edges.push({ when: { field: k, op: 'lt', value: r.min }, to: failId });
      if (r.max < spec.max) edges.push({ when: { field: k, op: 'gt', value: r.max }, to: failId });
      continue;
    }
    const vs = spec.values || [];
    const want = (rule && rule.passes && rule.passes[k]) || defaultPasses(vs);
    const pass = vs.filter((v) => want.includes(v));
    // An empty pass set always fails: "not in []" is not a condition, "in every outcome" is.
    edges.push(pass.length
      ? { when: { field: k, op: 'not_in', value: pass }, to: failId }
      : { when: { field: k, op: 'in', value: vs }, to: failId });
  }
  edges.push({ default: passId });
  return edges;
}

/**
 * The questions changed (the step's output with them): a question that is
 * still there keeps what it passed, as far as those outcomes still exist; a
 * new one, or one whose pass set no longer exists, starts at its default.
 */
export function withDecisionOutput(form, node) {
  const passes = {};
  for (const [k, spec] of Object.entries(node.output || {})) {
    const prev = form.rule && form.rule.passes ? form.rule.passes[k] : undefined;
    if (spec.type === 'number') {
      const ok = prev && !Array.isArray(prev) && prev.min >= spec.min && prev.max <= spec.max && prev.min <= prev.max;
      passes[k] = ok ? prev : defaultRange(spec);
      continue;
    }
    const vs = spec.values || [];
    const kept = (prev || []).filter((v) => vs.includes(v));
    passes[k] = !prev ? defaultPasses(vs) : (kept.length || !prev.length ? kept : defaultPasses(vs));
  }
  return { ...form, node, rule: { type: 'decision', passes } };
}

/**
 * The form a graph reads as, or null when the graph is not one check with
 * a pass and a fail — that eval belongs to the triage editor.
 */
export function readSimple(graph) {
  if (!graph || !graph.entry) return null;
  const nodes = graph.nodes || {};
  const step = nodes[graph.entry];
  if (!step || step.type === 'verdict') return null;
  const others = Object.keys(nodes).filter((id) => id !== graph.entry);
  if (others.length !== 2 || !others.every((id) => nodes[id].type === 'verdict')) return null;
  const passId = others.find((id) => nodes[id].config?.passed !== false);
  const failId = others.find((id) => nodes[id].config?.passed === false);
  if (!passId || !failId || passId === failId) return null;
  const mapped = (step.edges || []).map((e) => (e.default !== undefined ? { default: e.default === passId ? PASS : FAIL } : { ...e, to: e.to === passId ? PASS : FAIL }));
  const verdicts = { [passId]: nodes[passId], [failId]: nodes[failId] };
  const failReason = nodes[failId].config?.reason_code || '';
  if (kindOf(step) === 'decision_model') {
    const rule = decisionRuleOf(step.output, mapped);
    if (!rule) return null;
    return { stepId: graph.entry, passId, failId, verdicts, kind: 'decision_model', node: step, field: null, spec: null, rule, failReason };
  }
  const f = fieldOf(step);
  if (!f) return null;
  const rule = ruleOf({ ...step, edges: mapped }, f[0], f[1]);
  if (!rule) return null;
  return {
    stepId: graph.entry, passId, failId, verdicts: { [passId]: nodes[passId], [failId]: nodes[failId] },
    kind: kindOf(step), node: step, field: f[0], spec: f[1], rule,
    failReason: nodes[failId].config?.reason_code || '',
  };
}

export const isSimpleGraph = (graph) => readSimple(graph) !== null;

/** A fresh check of this kind, with the kind's default answer and a rule that passes its "good" side. */
export function startSimple(kind, target = 'run') {
  const meta = NODE_KINDS[kind];
  // A judge reads the target's own fields: a span's input and output, a conversation's transcript.
  const d = kind === 'llm_judge'
    ? { ...meta.defaults(target), config: { ...TEMPLATES.find((t) => t.id === 'judge').graph(target).nodes.judge.config } }
    : meta.defaults(target);
  const node = { type: meta.type, name: meta.label, instrument: d.instrument || meta.instrument, config: d.config, output: d.output, edges: [] };
  if (kind === 'decision_model') {
    const passes = Object.fromEntries(Object.entries(node.output).map(([k, spec]) => [k, spec.type === 'number' ? defaultRange(spec) : defaultPasses(spec.values)]));
    return buildSimple({ kind, node, rule: { type: 'decision', passes }, failReason: DEFAULT_REASON[kind] });
  }
  const f = fieldOf(node);
  return buildSimple({ kind, node, field: f[0], spec: f[1], rule: defaultRule(f[1], kind), failReason: DEFAULT_REASON[kind] || 'FAILED' });
}

/** What passes by default for an answer shape: true, the top of a score, the first category. */
export function defaultRule(spec, kind = null) {
  if (spec.type === 'boolean') return { type: 'boolean', value: true };
  if (spec.type === 'enum') {
    const yes = (spec.values || []).find((v) => /^(yes|true|pass|ok|good)$/i.test(v));
    return { type: 'enum', values: yes ? [yes] : (spec.values || []).slice(0, 1) };
  }
  const min = Number.isFinite(spec.min) ? spec.min : 0;
  const max = Number.isFinite(spec.max) ? spec.max : 1;
  const cut = kind === 'llm_judge' ? tidy(min + (max - min) * 0.7) : tidy(min + (max - min) / 2);
  return { type: 'number', min: cut, max };
}

/** The edges for a rule: what passes is named, everything else falls to Fail. */
export function edgesFor(field, spec, rule, passId = PASS, failId = FAIL) {
  if (spec.type === 'boolean') return [{ when: { field, op: 'eq', value: rule.value !== false }, to: passId }, { default: failId }];
  if (spec.type === 'enum') {
    const values = (rule.values || []).filter((v) => (spec.values || []).includes(v));
    return values.length ? [{ when: { field, op: 'in', value: values }, to: passId }, { default: failId }] : [{ default: failId }];
  }
  const lo = Math.max(spec.min, Math.min(spec.max, Number.isFinite(rule.min) ? rule.min : spec.min));
  const hi = Math.min(spec.max, Math.max(lo, Number.isFinite(rule.max) ? rule.max : spec.max));
  return [{ when: { field, op: 'between', range: { min: tidy(lo), max: tidy(hi), inclusive_min: true, inclusive_max: true } }, to: passId }, { default: failId }];
}

/**
 * The graph for a form. An eval that already has node ids keeps them — its
 * history, paths and analysis are keyed by them — and a new one gets
 * check / pass / fail.
 */
export function buildSimple({ node, field, spec, rule, failReason, kind, stepId = STEP, passId = PASS, failId = FAIL, verdicts = {} }) {
  const k = kind || kindOf(node);
  const decision = k === 'decision_model';
  const output = decision ? (node.output || {}) : { [field]: spec };
  const edges = decision ? decisionEdges(output, rule, passId, failId) : edgesFor(field, spec, rule, passId, failId);
  const step = { ...node, name: node.name || kindMeta(k).label, output, edges };
  const numeric = !decision && spec.type === 'number';
  const score = numeric ? { score_from: { node_id: stepId, field } } : {};
  const pass = { type: 'verdict', name: 'Pass', ...(verdicts[passId] || {}), config: { ...(verdicts[passId]?.config || {}), passed: true, ...score } };
  const fail = { type: 'verdict', name: 'Fail', ...(verdicts[failId] || {}), config: { ...(verdicts[failId]?.config || {}), passed: false, reason_code: failReason || DEFAULT_REASON[k] || 'FAILED', ...score } };
  if (!numeric) { delete pass.config.score_from; delete fail.config.score_from; }
  return { schema_version: 2, entry: stepId, nodes: { [stepId]: step, [passId]: pass, [failId]: fail } };
}

/**
 * The judge prompt's last line says what to answer. While it is still one
 * this module wrote (or the default), it follows the answer: a score in the
 * new range, true/false, or one of the categories. A line someone wrote is
 * left alone.
 */
const ASK_RE = /^(?:Score how well|Grade how well|Answer true if) the assistant (answered the user|served the user across the conversation)\b/;
export function askLine(spec, did = 'answered the user') {
  if (spec.type === 'boolean') return `Answer true if the assistant ${did} correctly and helpfully, false otherwise.`;
  if (spec.type === 'enum') return `Grade how well the assistant ${did} as one of: ${(spec.values || []).join(', ')}.`;
  return `Score how well the assistant ${did}, from ${spec.min} (poor) to ${spec.max} (excellent).`;
}
function withAsk(node, spec) {
  const prompt = node?.config?.prompt;
  const user = typeof prompt === 'string' ? prompt : prompt?.user;
  if (typeof user !== 'string') return node;
  const lines = user.split('\n');
  const i = lines.length - 1 - [...lines].reverse().findIndex((l) => l.trim());
  const m = i >= 0 && i < lines.length ? ASK_RE.exec(lines[i].trim()) : null;
  if (!m) return node;
  lines[i] = askLine(spec, m[1]);
  const next = lines.join('\n');
  return { ...node, config: { ...node.config, prompt: typeof prompt === 'string' ? next : { ...prompt, user: next } } };
}

/**
 * A change to the answer's shape keeps what it can: a new type gets the
 * kind's own field name and a default rule; a changed range or value list
 * clips the rule to what still exists.
 */
export function withAnswer(form, spec) {
  const prev = form.spec;
  const field = spec.type === prev.type ? form.field : { boolean: 'ok', number: 'score', enum: 'choice' }[spec.type];
  let rule = form.rule;
  if (spec.type !== prev.type) rule = defaultRule(spec, form.kind);
  else if (spec.type === 'number') rule = { type: 'number', min: Math.max(spec.min, Math.min(spec.max, rule.min)), max: Math.min(spec.max, Math.max(spec.min, rule.max)) };
  else if (spec.type === 'enum') {
    // A new category is not assumed to pass; one that stays keeps its choice.
    rule = { type: 'enum', values: rule.values.filter((v) => spec.values.includes(v)) };
  }
  const node = form.kind === 'llm_judge' ? withAsk(form.node, spec) : form.node;
  // A reason still at a default follows the answer: a low score, an answer that was not accepted, a check that failed.
  const failReason = DEFAULT_REASONS.has(form.failReason) ? (TYPE_REASON[spec.type] || form.failReason) : form.failReason;
  return { ...form, node, field, spec, rule, failReason };
}
